import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

interface StatItem {
    fields: {
        value: string;
        label: string;
    };
}

const enum fields {
    items = "items",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.items]: StatItem[];
    };
    extendedStyles?: StatsIndicatorsStyles;
}

export interface StatsIndicatorsStyles {
    itemsContainer?: InjectableCss;
    itemWrapper?: InjectableCss;
    valueText?: TypographyPresentationProps;
    labelText?: TypographyPresentationProps;
}

export const statsIndicatorsStyles: StatsIndicatorsStyles = {
    itemsContainer: {
        css: css`
            color: white;
            width: 100vw;
            margin-left: calc(50% - 50vw);
            margin-right: calc(50% - 50vw);
            margin-bottom: 33px;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            background-color: ${({ theme }) => theme.colors.primary.main};
            padding: 40px;
            gap: 40px;
        `,
    },
    itemWrapper: {
        css: css`
            flex-basis: calc(25% - 30px);
            display: flex;
            flex-direction: column;
            align-items: center;

            @media (max-width:768px){
                flex-basis: calc(50% - 20px);
            }
        `,
    },
    valueText: {
        css: css`
            font-size: 40px;
            font-weight: 600;
            line-height: 1;
            text-align: center;
            margin-bottom: 7px;
        `,
    },
    labelText: {
        css: css`
            font-size: 12px;
            text-align: center;
            text-transform: uppercase;
        `,
    },
};

export const StatsIndicators: FC<OwnProps> = ({ fields: widgetFields, extendedStyles }) => {
    const items = widgetFields.items || [];

    if (items.length === 0) {
        return null;
    }

    const itemsContainerStyles = {
        css: css`
            ${statsIndicatorsStyles.itemsContainer?.css || ""}
            ${extendedStyles?.itemsContainer?.css || ""}
        `,
    };

    const itemWrapperStyles = {
        css: css`
            ${statsIndicatorsStyles.itemWrapper?.css || ""}
            ${extendedStyles?.itemWrapper?.css || ""}
        `,
    };

    return (
        <StyledWrapper className="stats-indicators-container" {...itemsContainerStyles}>
            {items.map((item, index) => (
                <StyledWrapper
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="stats-indicator-item"
                    {...itemWrapperStyles}
                >
                    <Typography className="stats-indicator-value" {...statsIndicatorsStyles.valueText}>
                        {parse(item.fields.value || "", parserOptions)}
                    </Typography>
                    <Typography className="stats-indicator-label" {...statsIndicatorsStyles.labelText}>
                        {parse(item.fields.label || "", parserOptions)}
                    </Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: StatsIndicators,
    definition: {
        group: "Basic",
        icon: "bars-progress",
        displayName: "Stats Indicators",
        fieldDefinitions: [
            {
                name: fields.items,
                displayName: "Indicators",
                editorTemplate: "ListField",
                getDisplay: ({ fields: { value, label } }) => label || value || "Indicator",
                defaultValue: [
                    { fields: { value: "0", label: "Label" } },
                    { fields: { value: "0", label: "Label" } },
                    { fields: { value: "0", label: "Label" } },
                    { fields: { value: "0", label: "Label" } },
                ],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "value",
                        displayName: "Value",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
